
import { Button, Card, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select, Typography, Tooltip } from 'antd';
import { toast } from 'react-toastify';
import { ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createValidityEntry, fetchValidityEntry, getDefaultValidity, updateDefaultValidityId, updateValidityApplyFor } from '../../redux/rtk/features/leavePolicy/leavePolicySlice';
import getCompanyIdFromToken from '../../utils/getCompanyIdFromToken';
import PageTitle from "../page-header/PageHeader";
import AddLeavePolicy from "./AddLeavePolicy";
import LeavePolicyTable from './LeavePolicyTable';

const { Title } = Typography;
dayjs.extend(utc);
dayjs.extend(timezone);

const LeavePolicyPage = () => {
  const [isValidityModalVisible, setIsValidityModalVisible] = useState(false);
  const [isDefaultValidityModalVisible, setIsDefaultValidityModalVisible] = useState(false);
  const [isApplyForModalVisible, setIsApplyForModalVisible] = useState(false);
  const [isApplyForConfirmVisible, setIsApplyForConfirmVisible] = useState(false);
  const [selectedValidities, setSelectedValidities] = useState([]);
  const [pendingSelectedValidities, setPendingSelectedValidities] = useState([]);

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const companyId = getCompanyIdFromToken();
  const validityData = useSelector((state) => state.leavePolicy.validity);
  const defaultValidity = useSelector((state) => state.leavePolicy.defaultValidity);
  console.log("validityData", validityData);
  console.log("defaultValidity", defaultValidity);


  useEffect(() => {
    console.log("dispatching")
    dispatch(fetchValidityEntry());
    dispatch(getDefaultValidity());
  }, [dispatch]);

  // Set the current default validity in the form when modal opens
  useEffect(() => {
    if (defaultValidity && defaultValidity.length > 0 && defaultValidity[0]?.validity_id) {
      form.setFieldsValue({
        validity_id: defaultValidity[0].validity_id,
      });
    }
  }, [defaultValidity, form]);

  // Initialize selected validities with applyFor: true, and ensure default validity is included
  useEffect(() => {
    if (validityData && validityData.length > 0 && defaultValidity && defaultValidity.length > 0) {
      const applyForTrue = validityData
        .filter(item => item.applyFor === true)
        .map(item => item.id);
      
      const defaultValidityId = defaultValidity[0]?.validity_id;
      
      // Ensure default validity is always included if it exists
      const combinedValidities = [...new Set([...applyForTrue, defaultValidityId])];
      setSelectedValidities(combinedValidities);
    }
  }, [validityData, defaultValidity]);
  

  const formatDate = (date) => {
    return dayjs(date)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  };


  const onFinish = async (values) => {
    const FormData = {
      ...values,
      validFrom: formatDate(values.validFrom),
      validTo: formatDate(values.validTo),
      company_id: companyId,
    };

    setLoader(true);
    const resp = await dispatch(createValidityEntry(FormData));
    if (resp.payload.message === "success") {
      // Refresh validity data after creating new validity
      await dispatch(fetchValidityEntry());
      await dispatch(getDefaultValidity());
      setLoader(false);
      form.resetFields();
      setIsValidityModalVisible(false);
    } else {
      setLoader(false);
    }
  };

  const onDefaultValiditySubmit = async () => {
    const validityId = form.getFieldValue("validity_id");
    console.log("validityId", validityId);

    setLoader(true);
    const response = await dispatch(updateDefaultValidityId({ company_id: companyId, values: { validity_id: validityId } }));

    console.log("Response Payload:", response);
    if (response.payload?.message === "success" || response.payload) {
      // Refresh the default validity data
      await dispatch(getDefaultValidity());
      setLoader(false);
      setIsDefaultValidityModalVisible(false);
    } else {
      setLoader(false);
    }
  };


  const showValidityModal = () => {
    setIsValidityModalVisible(true);
  };

  const handleValidityCancel = () => {
    setIsValidityModalVisible(false);
  };

  const showDefaultValidityModal = () => {
    setIsDefaultValidityModalVisible(true);
  };

  const handleDefaultValidityCancel = () => {
    setIsDefaultValidityModalVisible(false);
  };

  const handleValidityApplyForChange = (selectedIds) => {
    // Just update the local state without showing confirmation
    setSelectedValidities(selectedIds);
  };

  const showApplyForModal = () => {
    setIsApplyForModalVisible(true);
  };

  const handleApplyForModalCancel = () => {
    setIsApplyForModalVisible(false);
  };

  const handleUpdateClick = () => {
    // Store current selection and show confirmation modal
    setPendingSelectedValidities(selectedValidities);
    setIsApplyForConfirmVisible(true);
  };

  const handleApplyForConfirm = async () => {
    const selectedIds = pendingSelectedValidities;
    
    setLoader(true);
    
    try {
      // Get all validity IDs
      const allValidityIds = validityData.map(item => item.id);
      
      // Update each validity
      const updatePromises = allValidityIds.map(async (validityId) => {
        const applyFor = selectedIds.includes(validityId);
        return await dispatch(updateValidityApplyFor({ validity_id: validityId, applyFor }));
      });
      
      await Promise.all(updatePromises);
      
      // Refresh validity data
      await dispatch(fetchValidityEntry());
      
      // Show single success message
      toast.success("Validity Apply For Updated Successfully");
      
      setLoader(false);
      setIsApplyForConfirmVisible(false);
      setIsApplyForModalVisible(false);
      setPendingSelectedValidities([]);
    } catch (error) {
      toast.error("Error updating Validity Apply For");
      setLoader(false);
    }
  };

  const handleApplyForCancel = () => {
    setIsApplyForConfirmVisible(false);
    setPendingSelectedValidities([]);
  };


  return (
    <>
      <PageTitle title='Back' />
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Button
              type="primary"
              onClick={showValidityModal}
              style={{ marginTop: '8px' }}
            >
              Add Validity
            </Button>
            <Button
              type="primary"
              onClick={showDefaultValidityModal}
              style={{ marginTop: '8px' }}
            >
              Set Default Validity
            </Button>
            <Button
              type="primary"
              onClick={showApplyForModal}
              style={{ marginTop: '8px' }}
            >
              Select Validity User Can Apply For
            </Button>
          </div>
        </Form.Item>

        <Modal
          title="Set Validity"
          visible={isValidityModalVisible}
          onOk={() => form.submit()} // Submit the form when the "Confirm" button is clicked
          onCancel={handleValidityCancel}
          confirmLoading={loader}
          footer={[
            <Button key="cancel" onClick={handleValidityCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={() => form.submit()}>
              Confirm
            </Button>
          ]}
        >
          <Form.Item label="Validity" style={{ marginBottom: 0 }}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please Enter Name!' }]}
            >
              <Input placeholder="Enter Name" />
            </Form.Item>
            <Form.Item
              name="validFrom"
              label="From Date"
              rules={[{ required: true, message: 'Please select the start date!' }]}
              style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
            >
              <DatePicker placeholder="Select start date" />
            </Form.Item>
            <span style={{ display: 'inline-block', width: '16px', textAlign: 'center' }}></span>
            <Form.Item
              name="validTo"
              label="To Date"
              rules={[{ required: true, message: 'Please select the end date!' }]}
              style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
            >
              <DatePicker placeholder="Select end date" />
            </Form.Item>
          </Form.Item>
        </Modal>

        <Modal
          title="Set Default Validity"
          visible={isDefaultValidityModalVisible}
          onCancel={handleDefaultValidityCancel}
          footer={[
            <Button key="cancel" onClick={handleDefaultValidityCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" loading={loader} onClick={onDefaultValiditySubmit}>
              Confirm
            </Button>,
          ]}
        >
          <Form.Item
            name="validity_id"
            label="Select Validity"
            rules={[{ required: true, message: 'Please select validity!' }]}
          >
            <Select 
              placeholder="Select Validity" 
              loading={!validityData}
              showSearch
              optionFilterProp="children"
            >
              {validityData?.map((entry) => {
                const formattedValidFrom = new Intl.DateTimeFormat('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }).format(new Date(entry.validFrom));

                const formattedValidTo = new Intl.DateTimeFormat('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                }).format(new Date(entry.validTo));

                // Check if this is the current default validity
                const isDefault = defaultValidity && defaultValidity.length > 0 && 
                                  defaultValidity[0]?.validity_id === entry.id;

                return (
                  <Select.Option key={entry.id} value={entry.id}>
                    {entry.name} ({formattedValidFrom} to {formattedValidTo})
                    {isDefault && " ✓ (Current Default)"}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

        </Modal>

        <Modal
          title="Select Validity User Can Apply For"
          visible={isApplyForModalVisible}
          onCancel={handleApplyForModalCancel}
          footer={[
            <Button key="cancel" onClick={handleApplyForModalCancel}>
              Cancel
            </Button>,
            <Button 
              key="update" 
              type="primary" 
              onClick={handleUpdateClick}
              disabled={!selectedValidities || selectedValidities.length === 0}
            >
              Update
            </Button>,
          ]}
          width={600}
        >
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {validityData?.map((entry) => {
              const formattedValidFrom = new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }).format(new Date(entry.validFrom));

              const formattedValidTo = new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }).format(new Date(entry.validTo));

              // Check if this is the default validity
              const isDefault = defaultValidity && defaultValidity.length > 0 && 
                                defaultValidity[0]?.validity_id === entry.id;

              return (
                <div key={entry.id} style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={selectedValidities.includes(entry.id)}
                    disabled={isDefault}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedValidities([...selectedValidities, entry.id]);
                      } else {
                        setSelectedValidities(selectedValidities.filter(id => id !== entry.id));
                      }
                    }}
                  >
                    <span style={{ fontWeight: 'bold' }}>{entry.name}</span>
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                      ({formattedValidFrom} to {formattedValidTo})
                    </span>
                  </Checkbox>
                  {isDefault && (
                    <Tooltip title="To change this, please update the default validity.">
                      <EyeOutlined style={{ marginLeft: '8px', color: '#1890ff', cursor: 'help' }} />
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        </Modal>

        <Modal
          title={
            <span>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: '8px' }} />
              Confirm Validity Apply For Update
            </span>
          }
          visible={isApplyForConfirmVisible}
          onCancel={handleApplyForCancel}
          footer={[
            <Button key="cancel" onClick={handleApplyForCancel}>
              Cancel
            </Button>,
            <Button key="confirm" type="primary" loading={loader} onClick={handleApplyForConfirm}>
              Confirm Update
            </Button>,
          ]}
        >
          <p>Are you sure you want to update the validity apply for settings?</p>
          <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
            Selected Validities: {pendingSelectedValidities.length}
          </p>
        </Modal>

      </Form>
      <Row gutter={16}>
        <Col span={8}>
          <Card title="Create or Edit Leave Policy">
            <AddLeavePolicy />
          </Card>
        </Col>
        <Col span={16}>
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Leave Policies</span>
                {defaultValidity && defaultValidity.length > 0 && (
                  <div style={{ 
                    backgroundColor: '#ff7a00', 
                    color: 'white', 
                    padding: '4px 12px', 
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    fontSize: '12px'
                  }}>
                    {defaultValidity[0]?.validity?.name || 'Default Policy'}
                  </div>
                )}
              </div>
            }
          >
            <LeavePolicyTable />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default LeavePolicyPage;
